import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { apiUrl, SkuRankingQueries, FILTER_CONFIGS_HQ } from 'src/app/model/constants';
import { objectToQueryString } from 'src/app/helpers/utils';
interface FilterOption {
  value: string;
  text: string;
}

interface FilterRelationship {
  grandparent?: string;
  parent: string;
  child: string;
}

@Injectable({
  providedIn: 'root'
})

export class FiltersHelper {
  public readonly filterRelationships: FilterRelationship[] = [
    { grandparent: 'BOTTLERS_EN', parent: 'SALES_ORG_EN', child: 'SALES_SUBORG_EN' },
    { parent: 'SALES_ORG_EN', child: 'SALES_SUBORG_EN' },
    { parent: 'CHANNEL_LEV1', child: 'CHANNEL_LEV2' },
    { parent: 'MCI_MAIN_9_CATEGORY', child: 'MCI_SUBCATEGORY' },
    { grandparent: 'MCI_PKG_LEV1', parent: 'MCI_PKG_LEV2', child: 'PKG_LEV3' },
    { parent: 'MCI_MAIN_5_MAKER', child: 'BRAND_EN' }
  ];

  updateRelatedFilters(keyName: string, selectedItems: string[], originalData: Record<string, any[]>, lstOptions: Record<string, FilterOption[]>): void {
    const relationships = this.filterRelationships.filter(rel =>
      rel.parent === keyName || rel.grandparent === keyName
    );

    if (relationships.length === 0) {
      return;
    }

    relationships.forEach(relationship => {
      if (relationship.grandparent === keyName) {
        this.updateParentFilters(relationship.parent, keyName, selectedItems, originalData, lstOptions);
        this.updateChildFilters(relationship.child, relationship.parent, keyName, selectedItems, originalData, lstOptions);
      } else if (relationship.parent === keyName) {
        this.updateChildFilters(relationship.child, keyName, null, selectedItems, originalData, lstOptions);
      }
    });
  }

  private updateParentFilters(
    parentKey: string,
    grandparentKey: string,
    selectedItems: string[],
    originalData: Record<string, any[]>,
    lstOptions: Record<string, FilterOption[]>
  ): void {
    const isAllSelected = selectedItems.includes('All');

    if (isAllSelected) {
      if (originalData[parentKey]) {
        const uniqueValues = this.extractUniqueValues(originalData[parentKey], parentKey);
        lstOptions[parentKey] = this.createFilterOptions(uniqueValues);
      }
    } else {
      const filteredData = originalData[parentKey]?.filter((item: any) => {
        const grandparentValue = item[grandparentKey];
        return selectedItems.includes(grandparentValue || 'NULL');
      }) || [];

      const uniqueValues = this.extractUniqueValues(filteredData, parentKey);
      lstOptions[parentKey] = this.createFilterOptions(uniqueValues);
    }
  }

  private updateChildFilters(
    childKey: string,
    parentKey: string,
    grandparentKey: string | null,
    selectedItems: string[],
    originalData: Record<string, any[]>,
    lstOptions: Record<string, FilterOption[]>
  ): void {
    const isAllSelected = selectedItems.includes('All');

    if (isAllSelected) {
      if (originalData[childKey]) {
        let filteredData = originalData[childKey];
        const parentOptions = lstOptions[parentKey] || [];
        const availableParentValues = parentOptions.map(opt => opt.value);
        const actualParentValues = availableParentValues.filter(val => val !== 'All');

        if (actualParentValues.length > 0) {
          filteredData = filteredData.filter((item: any) => {
            const parentValue = item[parentKey];
            return actualParentValues.includes(parentValue || 'NULL');
          });
        }

        const uniqueValues = this.extractUniqueValues(filteredData, childKey);
        lstOptions[childKey] = this.createFilterOptions(uniqueValues);
      }
    } else {
      const filteredData = originalData[childKey]?.filter((item: any) => {
        const parentValue = item[parentKey];

        if (grandparentKey) {
          const grandparentValue = item[grandparentKey];
          const parentRelationship = this.filterRelationships.find(rel =>
            rel.grandparent === grandparentKey && rel.parent === parentKey
          );

          if (parentRelationship) {
            const parentOptions = lstOptions[parentKey] || [];
            const availableParentValues = parentOptions.map(opt => opt.value);

            return availableParentValues.includes(parentValue || 'NULL') && selectedItems.includes(grandparentValue || 'NULL');
          }
        }

        return selectedItems.includes(parentValue || 'NULL');
      }) || [];

      const uniqueValues = this.extractUniqueValues(filteredData, childKey);
      lstOptions[childKey] = this.createFilterOptions(uniqueValues);
    }
  }

  private getParentKey(childKey: string): string | null {
    const relationship = this.filterRelationships.find(rel => rel.child === childKey);
    return relationship ? relationship.parent : null;
  }

  public extractUniqueValues(data: any[], filterName: string): string[] {
    const values = data.map(item => item[filterName]).filter(Boolean);
    return [...new Set(values)].sort();
  }

  public createFilterOptions(values: string[]): FilterOption[] {
    return [
      { value: 'All', text: 'All' },
      ...values.map(value => ({
        value: value || 'NULL',
        text: value || 'NULL'
      }))
    ];
  }

  public getDefaultVisibleFilterList() {
    return ['BOTTLERS_EN', 'SALES_ORG_EN', 'CHANNEL_LEV1', 'MCI_MAIN_9_CATEGORY'];
  }

  public getListIsUsedFilter() {
    return {
      BOTTLERS_EN: true,
      SALES_ORG_EN: true,
      CHANNEL_LEV1: true,
      MCI_MAIN_9_CATEGORY: true
    };
  }

  static isShowBtnResetFilter(lstOption_States: Record<string, string[]>, lstOption_Default: string[]) {
    return Object.keys(lstOption_States).length > 0 && Object.keys(lstOption_States).length !== lstOption_Default.length;
  }

  public getFilterConfigs(config: any, visibleLimit: number, filterList: Array<string>): { [key: string]: string } {
    const result: { [key: string]: string } = {};
    const visibleList = filterList.slice(0, visibleLimit);

    filterList.forEach(key => {
      result[key] = visibleList.includes(key) ? 'enable' : 'disable';
    });

    return result;
  }


  public convertAllEnableToEnableTemp(lstOption_States: any) {
    Object.keys(lstOption_States).forEach(key => {
      if (lstOption_States[key] === 'enable') {
        lstOption_States[key] = 'enableTemp';
      }
    });
  }

  public convertAllDisabledToTentative(lstOption_States: any) {
    Object.keys(lstOption_States).forEach(key => {
      if (lstOption_States[key] === 'disable') {
        lstOption_States[key] = 'tentative';
      }
    });
  }

  public convertEnableTempToTentative(lstOption_States: any, keyName: string) {
    if (lstOption_States[keyName] === 'enableTemp') {
      lstOption_States[keyName] = 'tentative';
    }
  }

  public convertTentativeToEnableTemp(lstOption_States: any, keyName: string) {
    if (lstOption_States[keyName] === 'tentative') {
      lstOption_States[keyName] = 'enableTemp';
    }
  }

  public convertAllEnableTempToEnable(lstOption_States: any) {
    Object.keys(lstOption_States).forEach(key => {
      if (lstOption_States[key] === 'enableTemp') {
        lstOption_States[key] = 'enable';
      }
    });
  }

  public convertAllTentativeToDisable(lstOption_States: any) {
    Object.keys(lstOption_States).forEach(key => {
      if (lstOption_States[key] === 'tentative') {
        lstOption_States[key] = 'disable';
      }
    });
  }

  public resetFilterConfigs1(lstOption_States: any, visibleLimit: number) {
    Object.keys(lstOption_States).forEach(key => {
      lstOption_States[key] = 'tentative';
    });
  }

  public isShowBtnApply(lstOption_States: any) {
    return Object.keys(lstOption_States).some(key => lstOption_States[key] === 'enableTemp');
  }

  public isShowBtnResetFilter(lstOption_States: any, lstOption_Default: any) {
    return Object.keys(lstOption_States).length > 0 && Object.keys(lstOption_States).length !== lstOption_Default.length;
  }

  public isAllSelected(selectedArr: string[]) {
    return selectedArr.length === 1 && selectedArr[0] === 'All';
  }

  public getDefaultSelectedFilter() {
    return {
      BOTTLERS_EN: ['All'],
      SALES_ORG_EN: ['All'],
      CHANNEL_LEV1: ['All'],
      MCI_MAIN_9_CATEGORY: ['All']
    };
  }

  public resetFilter(
    lstOption_States: any,
    lstOption_Selected: any,
    isAddMoreFilter: boolean,
    visibleLimit: number = 4
  ): { isResetFilter: boolean; isShowApplyBtn: boolean } {
    const defaultFilters = this.getDefaultVisibleFilterList();
    if (isAddMoreFilter) {
      Object.keys(lstOption_States).forEach(key => {
        lstOption_States[key] = 'tentative';
      });
      Object.keys(lstOption_States).forEach(key => {
        lstOption_States[key] = defaultFilters.includes(key) ? 'enableTemp' : 'tentative';
      });
    } else {
      Object.keys(lstOption_States).forEach(key => {
        lstOption_States[key] = defaultFilters.includes(key) ? 'enable' : 'disable';
      });
    }
    if (!isAddMoreFilter) {
      Object.keys(lstOption_Selected).forEach(key => {
        lstOption_Selected[key] = ['All'];
      });
    }
    return {
      isResetFilter: Object.keys(lstOption_States).length > visibleLimit,
      isShowApplyBtn: Object.values(lstOption_States).includes('enableTemp')
    };
  }

}
